<!--
  商城总裁驾驶舱 - DataV 黑科技风格
  - 菜单内嵌：/mall/home
  - 新窗口纯净全屏：/mall/cockpit-view
-->
<template>
  <div class="mall-cockpit" :class="{ 'is-pure': isPureView }" v-loading="loading">
    <Decoration8 class="cockpit-top-line" :color="['#1c9cff', '#0f6cad']" />

    <header class="cockpit-header">
      <Decoration3 class="cockpit-header-deco" :color="['#1c9cff', '#0f6cad']" />
      <div class="cockpit-header-main">
        <h1>商城总裁驾驶舱</h1>
        <p>
          经营指标实时监控
          <template v-if="shopCtx.tradeScopeHint"> · {{ shopCtx.tradeScopeHint }}</template>
          · {{ nowText }}
        </p>
      </div>
      <Decoration3 class="cockpit-header-deco is-rev" :color="['#1c9cff', '#0f6cad']" />
      <div class="cockpit-header-actions">
        <el-alert
          v-if="shopCtx.listTip"
          class="cockpit-tip"
          :title="shopCtx.listTip"
          :type="shopCtx.multiSelfAbnormal || shopCtx.emptySelfShops ? 'warning' : 'info'"
          show-icon
          :closable="false"
        />
        <el-button class="cockpit-refresh" type="primary" plain :loading="loading" @click="reloadHome">
          刷新
        </el-button>
      </div>
    </header>

    <div class="kpi-row">
      <BorderBox12 v-for="item in kpiItems" :key="item.label" class="kpi-panel">
        <div class="kpi-inner">
          <div class="kpi-label">
            {{ item.label }}
            <span v-if="item.hint" class="kpi-hint">{{ item.hint }}</span>
          </div>
          <DigitalFlop :config="item.flop" class="kpi-flop" />
          <div class="kpi-foot">
            <span :class="item.up ? 'up' : 'down'">{{ item.rateText }}</span>
            <em>较昨日 {{ item.refText }}</em>
          </div>
        </div>
      </BorderBox12>
    </div>

    <div class="cockpit-grid cockpit-grid-2">
      <BorderBox12 class="panel">
        <div class="panel-title">交易量趋势</div>
        <TradeTrendCard tech />
      </BorderBox12>
      <BorderBox12 class="panel">
        <div class="panel-title">运营待办</div>
        <OperationDataCard tech :navigate="!isPureView" />
      </BorderBox12>
    </div>

    <div class="cockpit-grid cockpit-grid-member">
      <BorderBox12 class="panel panel-wide">
        <div class="panel-title">会员概览</div>
        <div class="panel-body tech-surface">
          <MemberFunnelCard tech />
        </div>
      </BorderBox12>
      <BorderBox12 class="panel">
        <div class="panel-title">会员终端</div>
        <div class="panel-body tech-surface">
          <MemberTerminalCard />
        </div>
      </BorderBox12>
    </div>

    <!-- 驾驶舱只保留数据大屏；快捷入口已迁到「概况」 -->
    <div class="cockpit-grid cockpit-grid-full">
      <BorderBox12 class="panel panel-wide">
        <div class="panel-title">用户统计</div>
        <MemberStatisticsCard tech />
      </BorderBox12>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BorderBox12, Decoration3, Decoration8, DigitalFlop } from '@kjgl77/datav-vue3'
import * as TradeStatisticsApi from '@/api/mall/statistics/trade'
import * as MemberStatisticsApi from '@/api/mall/statistics/member'
import { DataComparisonRespVO } from '@/api/mall/statistics/common'
import { TradeOrderSummaryRespVO } from '@/api/mall/statistics/trade'
import { MemberCountRespVO } from '@/api/mall/statistics/member'
import { fenToYuanNumber, calculateRelativeRate } from '@/utils'
import { formatDate } from '@/utils/formatTime'
import { useMallSelfShopContext } from '@/hooks/web/useMallSelfShopContext'
import { MALL_CEO_COCKPIT_VIEW_NAME } from '@/layout/standalone/mallMenuScopes'
import MemberStatisticsCard from './components/MemberStatisticsCard.vue'
import OperationDataCard from './components/OperationDataCard.vue'
import TradeTrendCard from './components/TradeTrendCard.vue'
import MemberTerminalCard from '@/views/mall/statistics/member/components/MemberTerminalCard.vue'
import MemberFunnelCard from '@/views/mall/statistics/member/components/MemberFunnelCard.vue'

/** 商城总裁驾驶舱（原商城首页） */
defineOptions({ name: 'MallHome' })

const route = useRoute()
const { context: shopCtx, buildShopFilter, refresh: refreshShopCtx } = useMallSelfShopContext()

const isPureView = computed(
  () => route.name === MALL_CEO_COCKPIT_VIEW_NAME || route.path === '/mall/cockpit-view'
)

const loading = ref(true)
const nowText = ref(formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss'))
const orderComparison = ref<DataComparisonRespVO<TradeOrderSummaryRespVO>>()
const userComparison = ref<DataComparisonRespVO<MemberCountRespVO>>()

let clockTimer: ReturnType<typeof setInterval> | undefined

const buildFlop = (value: number, decimals = 0) => ({
  number: [Number(value) || 0],
  content: '{nt}',
  toFixed: decimals,
  style: {
    fontSize: 28,
    fill: '#00f2fe',
    fontWeight: 'bold'
  }
})

const formatRate = (value: number, reference: number) => {
  const rate = Number(calculateRelativeRate(value, reference) || 0)
  const up = rate >= 0
  return {
    up,
    rateText: `${up ? '+' : ''}${Math.abs(rate).toFixed(1)}%`
  }
}

const kpiItems = computed(() => {
  const orderPay = fenToYuanNumber(orderComparison.value?.value?.orderPayPrice || 0)
  const orderPayRef = fenToYuanNumber(orderComparison.value?.reference?.orderPayPrice || 0)
  const visit = Number(userComparison.value?.value?.visitUserCount || 0)
  const visitRef = Number(userComparison.value?.reference?.visitUserCount || 0)
  const orderCnt = Number(orderComparison.value?.value?.orderPayCount || 0)
  const orderCntRef = Number(orderComparison.value?.reference?.orderPayCount || 0)
  const register = Number(userComparison.value?.value?.registerUserCount || 0)
  const registerRef = Number(userComparison.value?.reference?.registerUserCount || 0)

  const salesRate = formatRate(orderPay, orderPayRef)
  const visitRate = formatRate(visit, visitRef)
  const orderRate = formatRate(orderCnt, orderCntRef)
  const registerRate = formatRate(register, registerRef)

  return [
    {
      label: '今日销售额',
      hint: shopCtx.value.tradeScopeHint,
      flop: buildFlop(orderPay, 2),
      refText: `￥${orderPayRef.toFixed(2)}`,
      ...salesRate
    },
    {
      label: '今日访问量',
      hint: shopCtx.value.memberScopeHint,
      flop: buildFlop(visit),
      refText: String(visitRef),
      ...visitRate
    },
    {
      label: '今日订单量',
      hint: shopCtx.value.tradeScopeHint,
      flop: buildFlop(orderCnt),
      refText: String(orderCntRef),
      ...orderRate
    },
    {
      label: '今日新增用户',
      hint: shopCtx.value.memberScopeHint,
      flop: buildFlop(register),
      refText: String(registerRef),
      ...registerRate
    }
  ]
})

const getOrderComparison = async () => {
  const filter = buildShopFilter()
  orderComparison.value = await TradeStatisticsApi.getOrderComparison(filter)
}

const getUserCountComparison = async () => {
  userComparison.value = await MemberStatisticsApi.getUserCountComparison()
}

const reloadHome = async () => {
  loading.value = true
  try {
    await refreshShopCtx()
    await Promise.all([getOrderComparison(), getUserCountComparison()])
  } finally {
    loading.value = false
  }
}

watch(
  () => shopCtx.value.selfScope,
  () => {
    void reloadHome()
  }
)

onMounted(async () => {
  nowText.value = formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
  clockTimer = setInterval(() => {
    nowText.value = formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
  }, 1000)
  await reloadHome()
})

onBeforeUnmount(() => {
  if (clockTimer) clearInterval(clockTimer)
})
</script>

<style lang="scss" scoped>
.mall-cockpit {
  --cockpit-cyan: #00f2fe;
  --cockpit-blue: #1c9cff;
  --cockpit-text: #e6f7ff;
  --cockpit-muted: #9fc4ee;
  position: relative;
  min-height: 720px;
  padding: 10px 14px 18px;
  overflow: auto;
  color: var(--cockpit-text);
  background:
    radial-gradient(ellipse at 50% 0%, #0d3b66 0%, #071e3d 55%, #030c1e 100%);
  font-size: 13px;

  &.is-pure {
    position: fixed;
    inset: 0;
    z-index: 3000;
    min-height: 100vh;
    min-height: 100dvh;
  }
}

.cockpit-top-line {
  position: absolute;
  top: 4px;
  left: 50%;
  width: 58%;
  height: 18px;
  transform: translateX(-50%);
}

.cockpit-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  margin-bottom: 10px;
  padding-top: 8px;
}

.cockpit-header-deco {
  width: 220px;
  height: 36px;
  margin: 0 8px;
  flex-shrink: 0;

  &.is-rev {
    transform: rotateY(180deg);
  }
}

.cockpit-header-main {
  text-align: center;

  h1 {
    margin: 0;
    font-size: 28px;
    letter-spacing: 6px;
    color: #fff;
    text-shadow: 0 0 12px rgba(0, 242, 254, 0.55);
  }

  p {
    margin: 6px 0 0;
    color: var(--cockpit-muted);
    font-size: 12px;
  }
}

.cockpit-header-actions {
  position: absolute;
  right: 0;
  top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 42%;
}

.cockpit-tip {
  max-width: 360px;
  background: rgba(15, 40, 72, 0.85);
  border-color: rgba(28, 156, 255, 0.35);
}

.cockpit-refresh {
  border-color: rgba(0, 242, 254, 0.45);
  color: var(--cockpit-cyan);
  background: rgba(0, 242, 254, 0.08);
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.kpi-panel {
  min-height: 128px;
}

.kpi-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 12px;
}

.kpi-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cockpit-muted);
  font-size: 13px;
}

.kpi-hint {
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(28, 156, 255, 0.18);
  color: #7ec8ff;
  font-size: 11px;
}

.kpi-flop {
  height: 42px;
}

.kpi-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;

  em {
    font-style: normal;
    color: #6f8fb3;
  }

  .up {
    color: #ff6b81;
  }

  .down {
    color: #36cfc9;
  }
}

.cockpit-grid {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
}

.cockpit-grid-2 {
  grid-template-columns: 1.35fr 1fr;
}

.cockpit-grid-member {
  grid-template-columns: 1.6fr 1fr;
}

.cockpit-grid-full {
  grid-template-columns: 1fr;
}

.panel {
  min-height: 280px;
  padding: 4px;
}

.panel-wide {
  min-height: 320px;
}

.panel-title {
  height: 34px;
  margin-bottom: 4px;
  padding: 0 10px;
  color: var(--cockpit-cyan);
  font-size: 15px;
  font-weight: 600;
  line-height: 34px;
  background: linear-gradient(90deg, rgba(28, 156, 255, 0.28), transparent);
}

.panel-body {
  padding: 4px 8px 10px;
}

.tech-surface {
  :deep(.el-card),
  :deep(.el-card.is-always-shadow),
  :deep(.el-card.is-hover-shadow) {
    color: var(--cockpit-text);
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  :deep(.el-card__header) {
    border-bottom-color: rgba(0, 242, 254, 0.18);
    color: var(--cockpit-text);
  }

  :deep(.el-card__body),
  :deep(.text-gray-500),
  :deep(.text-gray-400) {
    color: var(--cockpit-muted);
  }
}

@media (max-width: 1280px) {
  .kpi-row,
  .cockpit-grid-2,
  .cockpit-grid-member {
    grid-template-columns: 1fr 1fr;
  }

  .cockpit-grid-full {
    grid-template-columns: 1fr;
  }

  .cockpit-header-deco {
    display: none;
  }

  .cockpit-header-actions {
    position: static;
    max-width: none;
    margin-top: 8px;
  }

  .cockpit-header {
    flex-direction: column;
  }
}

@media (max-width: 900px) {
  .kpi-row,
  .cockpit-grid-2,
  .cockpit-grid-member {
    grid-template-columns: 1fr;
  }
}
</style>
